import { env } from 'lib/constants/env';
import type { PromoCode } from 'types/cart/PromoCode';
import type { ProductPage } from 'types/cms/pages/ProductPage';
import type { AmazonButton } from 'types/cms/product/AmazonButton';
import type { CTAType } from 'types/cms/product/CTAType';
import { PurchaseType } from 'types/product/PurchaseType';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';
import type { VariantOrSubscription } from 'types/product/VariantOrSubscription';
import { getSubscriptions } from 'utils/getSubscriptions';

import { ProductVariationSelector } from '../ProductVariationSelector';
import { OTPMultiVariant } from './OTPMultiVariant';
import { OTPSingleVariant } from './OTPSingleVariant';
import { SubscriptionMultiVariant } from './SubscriptionMultiVariant';
import { SubscriptionSingleVariant } from './SubscriptionSingleVariant';
import { Unavailable } from './Unavailable';

export interface ProductBlockOnlyProps {
  ctaType?: CTAType;
  purchaseType?: PurchaseType;
  amazonButton?: AmazonButton;
  asin?: string;
  showSavingsAsAmount?: boolean;
  isDiscountHidden?: boolean;
  selectedVariantsIds?: string[];
}
export interface SupplementPickerOnlyProps {
  isSupplementPicker?: boolean;
  selectedVariant?: VariantOrSubscription;
  setSelectedVariant?: (variant: VariantOrSubscription) => void;
}

export interface BaseConfiguratorProps
  extends ProductBlockOnlyProps, SupplementPickerOnlyProps {
  isPreorder?: boolean;
  infoBox?: string;
  promoCodes?: PromoCode[];
  productFormats?: ProductPage['productFormats'];
  showStickyCta?: boolean;
}

export interface ProductConfiguratorRenderProps extends BaseConfiguratorProps {
  slug: string;
  shopifyProduct?: ShopifyProduct | null;
  isLoading: boolean;
  mostPopularVariantId?: string;
  bestValueVariantId?: string;
}

export function ProductConfiguratorRender({
  slug,
  shopifyProduct,
  isLoading,
  mostPopularVariantId,
  bestValueVariantId,
  selectedVariantsIds,
  productFormats,
  ...props
}: ProductConfiguratorRenderProps) {
  /*
   * LOADING STATE
   * This UI displays nothing whilst the shopify product is loading
   */
  if (isLoading) {
    return null;
  }

  /*
   * PRODUCT UNAVAILABLE
   * This UI displays a disabled button to say the product is unavailable for the chosen country.
   *
   * For a shopify product that returns null because it is not available for the given locale.
   */
  if (!shopifyProduct) {
    return <Unavailable />;
  }

  /*
   * [SKINCARE] PRODUCT VARIATION
   * For skincare products only - if there are mini/refill/sample variations, we show
   * an OTP selector which links to each respective PDP.
   */
  if (
    env.NEXT_PUBLIC_SKINCARE_PDP_PICKER === 'FORMAT' &&
    productFormats &&
    productFormats.length > 1
  ) {
    return (
      <ProductVariationSelector
        slug={slug}
        shopifyProduct={shopifyProduct}
        productFormats={productFormats}
        {...props}
      />
    );
  }

  const { variants, availableForSale } = shopifyProduct;
  const { ctaType, amazonButton, purchaseType } = props;

  const subscriptions = getSubscriptions(shopifyProduct);

  /* If any variants are selected in the CMS (only applies to the product block),
   * we filter the variants and subscriptions.
   */
  const selectedVariants = selectedVariantsIds?.length
    ? variants.filter((variant) => selectedVariantsIds.includes(variant.id))
    : variants;

  if (!selectedVariants) return null;

  const selectedSubscriptions = selectedVariantsIds?.length
    ? subscriptions.filter((variant) =>
        selectedVariantsIds.includes(variant.id),
      )
    : subscriptions;

  /* The One Time Purchase variant (for things like OTP links/buttons)
   * should be the first selected variant.
   */
  const otpVariant = selectedVariants[0];

  // Some props that all the components below require.
  const sharedProps = {
    availableForSale,
    otpVariant,
  };

  // Creating useful variables to make the if statements more readible
  const isAmazonOnly = ctaType === 'none' && amazonButton?.hasAmazonButton;
  const hasSubscriptions = !!subscriptions?.length;
  const hasOneSelectedVariant = selectedVariants.length === 1;
  const hasMoreThanOneSelectedVariant = selectedVariants.length > 1;
  const isOnlyOTP = !hasSubscriptions || purchaseType === PurchaseType.OTP;

  /*
   * ONE-TIME-PURCHASE SINGLE VARIANT
   * This is the most simple UI which just displays the price of a single product and CTAs.
   *
   * Typically for products like samples and simple accesories (without variants like sizes).
   * Could also be any other product if:
   * 1. Only one variant has been selected AND (it is a product without subscriptions OR
   *    if the 'only OTP' has been selected).
   * 2. Product is not available for sale (OOS).
   * 3. An amazon button is selected, it trumps everything and displays the first variant
   *    price and an amazon button (no other CTAs).
   */
  if (
    (hasOneSelectedVariant && isOnlyOTP) ||
    !availableForSale ||
    isAmazonOnly
  ) {
    return (
      <OTPSingleVariant variant={otpVariant} {...sharedProps} {...props} />
    );
  }

  /*
   * ONE-TIME-PURCHASE MULTI VARIANT (NO SUBSCRIPTIONS)
   * This UI displays a selection box for each variant.
   *
   * Typically for products like t-shirts and nutrition refills.
   * Could also be a product like nutrition that has multiple variants, if the 'only OTP'
   * has been selected.
   */
  if (hasMoreThanOneSelectedVariant && isOnlyOTP) {
    return (
      <OTPMultiVariant
        productType={shopifyProduct.productType}
        variants={selectedVariants}
        {...sharedProps}
        {...props}
      />
    );
  }

  /*
   * SUBSCRIPTIONS SINGLE VARIANT
   * This UI displays radio buttons with two items:
   * 1. Subscribe radio box containing a select element with the subscription options.
   * 2. One-time-purchase radio box.
   *
   * Typically for products like skincare.
   * Could also be a product like nutrition that has multiple variants, if only one variant
   * has been selected.
   *
   * The UI could be modified if:
   * 'Only SUB' is selected, the one-time-purchase radio box is removed and the subscribe
   * item becomes an unclickable box.
   * Only one variant is selected, the select element inside the subscribe radio box is removed.
   */
  if (hasOneSelectedVariant && hasSubscriptions) {
    return (
      <SubscriptionSingleVariant
        subscriptions={selectedSubscriptions}
        variant={selectedVariants[0]}
        {...sharedProps}
        {...props}
      />
    );
  }

  /*
   * SUBSCRIPTIONS MULTI VARIANT (ONE SUBSCRIPTION PER VARIANT)
   * This UI displays a radio button per subscription item.
   * Also displays one-time-purchase link under the CTAS which will use the first variant.
   *
   * Typically for products like nutrition.
   * The UI could be modified if:
   * 1. There is only one variant selected, the radio button becomes a box that is unclickable.
   * 2. The 'subscription only' option is selected, the one time purchase link will be removed.
   */
  if (hasMoreThanOneSelectedVariant && hasSubscriptions) {
    return (
      <SubscriptionMultiVariant
        bestValueVariantId={bestValueVariantId}
        mostPopularVariantId={mostPopularVariantId}
        subscriptions={selectedSubscriptions}
        selectedVariantsIds={selectedVariantsIds}
        {...sharedProps}
        {...props}
      />
    );
  }

  return null;
}
